import { jsx as _jsx } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
const ThemeContext = createContext(null);
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState('system');
    const [resolvedTheme, setResolvedTheme] = useState('dark');
    // Get system theme preference
    const getSystemTheme = () => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'dark';
    };
    // Initialize theme from localStorage or default to system
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
            setTheme(savedTheme);
        }
    }, []);
    // Update resolved theme when theme or system preference changes
    useEffect(() => {
        const updateResolvedTheme = () => {
            const newResolvedTheme = theme === 'system' ? getSystemTheme() : theme;
            setResolvedTheme(newResolvedTheme);
            // Apply theme to document
            const root = document.documentElement;
            if (newResolvedTheme === 'dark') {
                root.classList.add('dark');
            }
            else {
                root.classList.remove('dark');
            }
        };
        updateResolvedTheme();
        // Listen for system theme changes when using system theme
        if (theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => updateResolvedTheme();
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, [theme]);
    const handleSetTheme = (newTheme) => {
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };
    const contextValue = {
        theme,
        resolvedTheme,
        setTheme: handleSetTheme,
    };
    return (_jsx(ThemeContext.Provider, { value: contextValue, children: children }));
};
